/**
 * Contatti sul telefono (7/10/2026): risponde a "chi sto cercando?". È una
 * rubrica, come quella del telefono.
 *
 * Prima, a 390px: quattro riquadri di conteggi (Tutti 106, Clienti 72...),
 * poi schede alte 600px con COS'È / RECAPITO / VALORE / IN SOSPESO: si vedeva
 * un contatto e mezzo per volta. Qui:
 *   1. la ricerca in cima, perché in una rubrica si cerca;
 *   2. clienti, potenziali e professionisti in una striscia, coi conteggi;
 *   3. chi ci deve soldi scaduti, prima di tutti (è l'unica cosa da fare qui);
 *   4. tutti gli altri in ordine alfabetico, con la lettera come gruppo.
 * Il resto (recapiti, valore, trattative, referenti) è nella scheda del
 * contatto, che si apre toccando la riga.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { toast } from '../../stores/toast.store';
import { useUrlState } from '../../hooks/useUrlState';
import { Icon } from '../../components/ui/Icon';
import { fmtEur, euro } from '../../lib/utils';
import { MEmpty, MGroup, MPage, MRow, MSearch, MSeg, iniziali, leggibile } from '../../components/mobile/MobileKit';
import { NuovoContattoPopover } from './NuovoContattoPopover';
import { FILTRI, TIPO_LABEL, canWriteContatti, contattiQuery, type FiltroTipo, type RigaContatto } from './contactsApi';

const TIPI_VALIDI = new Set<string>(FILTRI.map((f) => f.key));

/** La lettera del gruppo: A-Z, tutto il resto sotto "#". */
function lettera(nome: string): string {
  const c = nome.trim().charAt(0).toUpperCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  return /[A-Z]/.test(c) ? c : '#';
}

export function ContactsMobile() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canWrite = canWriteContatti(user);
  const [tipoParam, setTipo] = useUrlState<FiltroTipo>('tipo', 'tutti');
  const tipo: FiltroTipo = TIPI_VALIDI.has(tipoParam) ? tipoParam : 'tutti';
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const [debSearch, setDebSearch] = useState(search);
  const [nuovo, setNuovo] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebSearch(search), 220);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useQuery(contattiQuery(tipo, debSearch));

  const crea = useMutation({
    mutationFn: (v: { nome: string; tipo: string; email: string | null; phone: string | null }) =>
      api.post<{ key: string }>('/contacts', v),
    onSuccess: (r, v) => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['clients'] });
      toast.ok('Contatto creato', v.nome);
      navigate(`/contatti/${r.key}`);
    },
    onError: (e: any) => toast.alert('Non creato', e?.message ?? 'Riprova'),
  });

  const items = data?.items ?? [];
  const conScaduto = useMemo(
    () => items.filter((r) => r.scaduto > 0).sort((a, b) => b.scaduto - a.scaduto),
    [items],
  );
  const perLettera = useMemo(() => {
    const m = new Map<string, RigaContatto[]>();
    for (const r of [...items].sort((a, b) => leggibile(a.name).localeCompare(leggibile(b.name), 'it', { sensitivity: 'base' }))) {
      const k = lettera(leggibile(r.name));
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(r);
    }
    return [...m.entries()].sort(([a], [b]) => (a === '#' ? 1 : b === '#' ? -1 : a.localeCompare(b)));
  }, [items]);

  const apri = (r: RigaContatto) => navigate(`/contatti/${r.key}`);
  const riga = (r: RigaContatto, conScad = false) => {
    const nome = leggibile(r.name);
    const cosE = r.tipi.map((t) => TIPO_LABEL[t]).join(' e ') || 'Contatto';
    const sotto = conScad
      ? <span className="is-alert">{euro(r.scaduto)} scaduti</span>
      : [cosE, r.city ? leggibile(r.city) : null].filter(Boolean).join(' · ');
    return <MRow key={r.key} text={iniziali(nome)} round title={nome} sub={sotto} onClick={() => apri(r)} />;
  };

  return (
    <>
      <MPage>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 'var(--s-3)' }}>
          <MSearch value={search} onChange={setSearch} placeholder="Cerca nome, città o P. IVA" />
          <MSeg<FiltroTipo>
            value={tipo}
            onChange={(k) => setTipo(k)}
            options={FILTRI.map((f) => ({ key: f.key, label: f.label, count: data?.conta?.[f.conta] }))}
          />
        </div>

        {isLoading && !data ? (
          <MGroup title="Contatti"><MEmpty>Carico la rubrica…</MEmpty></MGroup>
        ) : items.length === 0 ? (
          <MGroup title="Contatti">
            <MEmpty>{search.trim() ? `Nessun contatto con "${search.trim()}".` : 'Nessun contatto qui.'}</MEmpty>
          </MGroup>
        ) : (
          <>
            {!search.trim() && conScaduto.length > 0 && (
              <MGroup title="Ci devono soldi scaduti" count={conScaduto.length}>
                {conScaduto.slice(0, 6).map((r) => riga(r, true))}
              </MGroup>
            )}
            {perLettera.map(([l, righe]) => (
              <MGroup key={l} title={l}>
                {righe.map((r) => riga(r))}
              </MGroup>
            ))}
          </>
        )}
      </MPage>

      {canWrite && (
        <div className="m-actionbar m-only">
          <button className="btn btn-primary" onClick={() => setNuovo(true)}>
            <Icon name="plus" /> Nuovo contatto
          </button>
          {nuovo && (
            <NuovoContattoPopover
              pending={crea.isPending}
              onClose={() => setNuovo(false)}
              onConfirm={(v) => { setNuovo(false); crea.mutate(v); }}
            />
          )}
        </div>
      )}
    </>
  );
}
